import { pickVibeImage } from './pickVibeImage';
import { guided } from '@/llm/guidedRuntime';
import { parsePlan } from '@/llm/planLayouts';
import { PLAN_SYSTEM_PROMPT } from '@/llm/prompt/plan-prompt';
import { nearestKitId } from '@/webapp/styleFromImage';
import type { StyleBrief } from '@/llm/planTypes';

/**
 * "Show me the look you want" — a reference picture turned into a choice.
 *
 * This existed once and was lost: `choose-layout.tsx` was deleted, and `pickVibeImage`,
 * `setVibeImages` and `setPlanResult` have sat in the codebase called by nothing ever since.
 * The prompt that reads a picture is still here and still says what to do with one ("If an
 * IMAGE is provided, derive styleBrief from it"), so this is a rewiring, not an invention.
 *
 * It goes through the ordinary `/llm` proxy rather than a new server route, because the proxy
 * already accepts an image — its own field comment calls this exact use "plan-first style
 * extraction". So nothing here needs deploying.
 *
 * The COLOURS are not handed to the build. They choose the kit, and the kit carries colour the
 * way the runtime expects; see `styleFromImage`.
 */

export type PickedStyle =
  | { kind: 'style'; styleBrief: StyleBrief; kitId: string }
  | { kind: 'failed'; reason: string };

export interface PickStyleDeps {
  pick?: () => Promise<string | null>;
  generate?: (input: {
    systemPrompt: string;
    messages: { role: 'user' | 'assistant'; content: string }[];
    signedIn: boolean;
    image?: string;
  }) => Promise<{ content: string }>;
}

/**
 * Returns null when the person cancelled the picker — not a failure, and it must not be
 * reported as one. Anything that genuinely went wrong comes back as `failed` with a reason
 * somebody can act on.
 */
export async function pickStyleFromPicture(
  goal: string,
  signedIn: boolean,
  deps: PickStyleDeps = {},
): Promise<PickedStyle | null> {
  const pick = deps.pick ?? pickVibeImage;

  let image: string | null;
  try {
    image = await pick();
  } catch {
    return { kind: 'failed', reason: 'Could not open your photos just now.' };
  }
  if (!image) return null;

  let content: string;
  try {
    const generate = deps.generate ?? ((input) => guided().generate(input));
    const res = await generate({
      systemPrompt: PLAN_SYSTEM_PROMPT,
      // The goal travels with the picture: the same photo means different things for a
      // scripture reader and a bike-repair log, and the model is being asked for a look that
      // suits BOTH.
      messages: [{ role: 'user', content: `Idea: ${goal}` }],
      signedIn,
      image,
    });
    content = res.content;
  } catch {
    return { kind: 'failed', reason: 'Could not read that picture just now. Your plan is unchanged.' };
  }

  const parsed = parsePlan(content);
  if (!parsed?.styleBrief) {
    // A reply we cannot parse must not become a half-applied look — the plan is left exactly
    // as it was, and the person is told, rather than seeing a kit change for no visible reason.
    return { kind: 'failed', reason: 'That picture did not give a clear look. Try another one.' };
  }

  return {
    kind: 'style',
    styleBrief: parsed.styleBrief,
    kitId: nearestKitId(parsed.styleBrief.palette.primary),
  };
}
